import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Group,
  Avatar,
  Text,
  Button,
  Stack,
  Badge,
  Center,
  Box,
  SegmentedControl,
  useMantineColorScheme,
  Loader,
  ActionIcon
} from '@mantine/core';
import {
  IconUserCheck,
  IconUserX,
  IconUserPlus,
  IconUser,
  IconX
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { AmigosService } from '../services/amigosService';
import {
  SolicitudAmistadDTO,
  AmistadDTO,
  BuscarUsuarioDTO
} from '../types/amigos';
import GradientCard from '../components/ui/GradientCard';
import SearchInput from '../components/ui/SearchInput';
import PageHeader from '../components/ui/PageHeader';
import PageContainer from '../components/ui/PageContainer';
import PageSkeleton from '../components/ui/PageSkeleton';
import { useQuery } from '@tanstack/react-query';
import { queryClient } from '../lib/queryClient';
import { solicitudesRecibidasQuery, solicitudesEnviadasQuery, amigosQuery } from '../queries/appQueries';

const SolicitudesPage: React.FC = () => {
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === 'dark';
  const verPerfil = (id: number) => navigate(`/perfil/${id}`);
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const initialTab = tabFromUrl && ['recibidas', 'enviadas', 'amigos'].includes(tabFromUrl)
    ? tabFromUrl
    : 'amigos';
  const [activeTab, setActiveTab] = useState<string>(initialTab);

  useEffect(() => {
    if (tabFromUrl && ['recibidas', 'enviadas', 'amigos'].includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  // Búsqueda de usuarios inline (sin modal)
  const [busqueda, setBusqueda] = useState('');
  const [resultados, setResultados] = useState<BuscarUsuarioDTO[]>([]);
  const [buscando, setBuscando] = useState(false);
  const busquedaActiva = busqueda.trim().length >= 2;

  // Listas cacheadas con React Query
  const recibidasQ = useQuery(solicitudesRecibidasQuery());
  const enviadasQ = useQuery(solicitudesEnviadasQuery());
  const amigosQ = useQuery(amigosQuery());

  const solicitudesRecibidas: SolicitudAmistadDTO[] = recibidasQ.data ?? [];
  const solicitudesEnviadas: SolicitudAmistadDTO[] = enviadasQ.data ?? [];
  const amigos: AmistadDTO[] = amigosQ.data ?? [];
  const loading = recibidasQ.isLoading || enviadasQ.isLoading || amigosQ.isLoading;

  // Busca usuarios con debounce mientras se escribe (mínimo 2 caracteres).
  useEffect(() => {
    const q = busqueda.trim();
    if (q.length < 2) {
      setResultados([]);
      setBuscando(false);
      return;
    }
    setBuscando(true);
    const t = setTimeout(async () => {
      try {
        const r = await AmigosService.buscarUsuarios(q);
        setResultados(r);
      } catch (error) {
        notifications.show({ title: 'Error', message: 'No se pudo realizar la búsqueda', color: 'red' });
      } finally {
        setBuscando(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [busqueda]);

  const enviarSolicitud = async (destinatarioId: number) => {
    try {
      await AmigosService.enviarSolicitud(destinatarioId);
      notifications.show({ title: 'Solicitud enviada', message: 'Se envió la solicitud de amistad', color: 'green' });
      // Cerrar dropdown, limpiar input y saltar al tab de Enviadas para feedback visual.
      setBusqueda('');
      setResultados([]);
      setActiveTab('enviadas');
      cargarDatos();
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'No se pudo enviar la solicitud',
        color: 'red',
      });
    }
  };

  const estadoBadge = (estado: string) => {
    switch (estado) {
      case 'AMIGOS':
        return <Badge color="green">Amigos</Badge>;
      case 'SOLICITUD_ENVIADA':
        return <Badge color="blue">Solicitud enviada</Badge>;
      case 'SOLICITUD_RECIBIDA':
        return <Badge color="orange">Solicitud recibida</Badge>;
      default:
        return null;
    }
  };

  // Refresca las listas tras una mutación (aceptar/rechazar/eliminar) invalidando la caché.
  const cargarDatos = () => {
    queryClient.invalidateQueries({ queryKey: ['solicitudesRecibidas'] });
    queryClient.invalidateQueries({ queryKey: ['solicitudesEnviadas'] });
    queryClient.invalidateQueries({ queryKey: ['amigos'] });
  };

  const aceptarSolicitud = async (solicitudId: number) => {
    try {
      await AmigosService.aceptarSolicitud(solicitudId);
      notifications.show({
        title: 'Solicitud aceptada',
        message: '¡Ahora son amigos!',
        color: 'green'
      });
      cargarDatos();
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'No se pudo aceptar la solicitud',
        color: 'red'
      });
    }
  };

  const rechazarSolicitud = async (solicitudId: number) => {
    try {
      await AmigosService.rechazarSolicitud(solicitudId);
      notifications.show({
        title: 'Solicitud rechazada',
        message: 'Se rechazó la solicitud',
        color: 'blue'
      });
      cargarDatos();
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'No se pudo rechazar la solicitud',
        color: 'red'
      });
    }
  };

  const cancelarSolicitud = async (solicitudId: number) => {
    try {
      await AmigosService.cancelarSolicitud(solicitudId);
      notifications.show({
        title: 'Solicitud cancelada',
        message: 'Se canceló la solicitud',
        color: 'blue'
      });
      cargarDatos();
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'No se pudo cancelar la solicitud',
        color: 'red'
      });
    }
  };

  const eliminarAmistad = async (amistadId: number) => {
    try {
      await AmigosService.eliminarAmistad(amistadId);
      notifications.show({
        title: 'Amistad eliminada',
        message: 'Se eliminó la amistad',
        color: 'blue'
      });
      cargarDatos();
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'No se pudo eliminar la amistad',
        color: 'red'
      });
    }
  };

  // Fila compacta de usuario (una línea): avatar + nombre · @usuario | acciones a la derecha
  const FilaUsuario: React.FC<{
    nombre: string;
    username: string;
    avatarUrl?: string;
    onPerfil: () => void;
    acciones: React.ReactNode;
  }> = ({ nombre, username, avatarUrl, onPerfil, acciones }) => (
    <GradientCard p="xs" radius="md">
      <Group justify="space-between" wrap="nowrap" align="center" gap="sm">
        <Group
          gap="sm"
          wrap="nowrap"
          align="center"
          style={{ minWidth: 0, cursor: 'pointer' }}
          onClick={onPerfil}
        >
          <Avatar src={avatarUrl} alt={nombre || username} size="md" radius="xl" color="blue">
            {(nombre || username || 'U').charAt(0).toUpperCase()}
          </Avatar>
          <Group gap={6} wrap="nowrap" align="center" style={{ minWidth: 0 }}>
            <Text fw={600} lineClamp={1} style={{ textAlign: 'left' }}>{nombre || username}</Text>
            <Text size="sm" c="dimmed" lineClamp={1} style={{ textAlign: 'left' }}>@{username}</Text>
          </Group>
        </Group>
        <Group gap="xs" wrap="nowrap" align="center" style={{ flexShrink: 0 }}>
          {acciones}
        </Group>
      </Group>
    </GradientCard>
  );

  if (loading) {
    return <PageSkeleton variant="list" />;
  }

  return (
    <PageContainer>
      <Box mb="xl">
        <PageHeader
          title="Amigos y Solicitudes"
          subtitle="Conecta con otros estudiantes y gestiona tus solicitudes"
        />
      </Box>

      {/* Navbar */}
      <Group justify="flex-start" mb="md">
      <SegmentedControl
        value={activeTab}
        onChange={setActiveTab}
        data={[
          {
            value: 'recibidas',
            label: (
              <Group gap={6} wrap="nowrap" align="center">
                <span>Recibidas</span>
                {solicitudesRecibidas.length > 0 && (
                  <Badge size="sm" color="red" circle>{solicitudesRecibidas.length}</Badge>
                )}
              </Group>
            ),
          },
          {
            value: 'enviadas',
            label: (
              <Group gap={6} wrap="nowrap" align="center">
                <span>Enviadas</span>
                {solicitudesEnviadas.length > 0 && (
                  <Badge size="sm" color="blue" circle>{solicitudesEnviadas.length}</Badge>
                )}
              </Group>
            ),
          },
          { value: 'amigos', label: <span>Mis amigos ({amigos.length})</span> },
        ]}
        styles={{
          root: {
            background: dark ? 'rgba(30, 41, 59, 0.3)' : 'rgba(255, 255, 255, 0.3)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            padding: '4px',
          },
          control: {
            border: 'none !important',
            '&[data-active="true"]': {
              background: dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
              borderRadius: '8px',
            },
          },
          label: {
            color: dark ? '#e2e8f0' : '#1f2937',
            fontWeight: 500,
            '&[data-active="true"]': {
              color: dark ? '#ffffff' : '#000000',
            },
          },
        }}
      />
      </Group>

      {/* Buscador de usuarios con dropdown debajo del input */}
      <Box pos="relative" maw={420} mb="md">
        <SearchInput
          placeholder="Buscar usuarios para agregar..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.currentTarget.value)}
          rightSection={
            busqueda ? (
              <ActionIcon variant="subtle" color="gray" onClick={() => { setBusqueda(''); setResultados([]); }}>
                <IconX size={14} />
              </ActionIcon>
            ) : null
          }
        />

        {busquedaActiva && (
          <Box
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: 4,
              maxHeight: 320,
              overflowY: 'auto',
              backgroundColor: dark ? '#1e293b' : '#ffffff',
              border: `1px solid ${dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
              borderRadius: 8,
              boxShadow: dark
                ? '0 8px 24px rgba(0, 0, 0, 0.4)'
                : '0 8px 24px rgba(0, 0, 0, 0.12)',
              zIndex: 50,
              padding: 8,
            }}
          >
            {buscando ? (
              <Center h={120}><Loader size="sm" /></Center>
            ) : resultados.length === 0 ? (
              <Center h={80}>
                <Text size="sm" c="dimmed">No se encontraron usuarios</Text>
              </Center>
            ) : (
              <Stack gap={4}>
                {resultados.map((u) => (
                  <Group
                    key={u.id}
                    justify="space-between"
                    wrap="nowrap"
                    p="xs"
                    style={{ borderRadius: 6 }}
                  >
                    <Group
                      gap="sm"
                      wrap="nowrap"
                      style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                      onClick={() => verPerfil(u.id)}
                    >
                      <Avatar src={u.avatarUrl} radius="xl" size="sm">
                        {(u.nombre || u.username || '?').charAt(0).toUpperCase()}
                      </Avatar>
                      <Stack gap={0} style={{ minWidth: 0 }}>
                        <Text size="sm" fw={500} truncate>
                          {u.nombre && u.apellidos ? `${u.nombre} ${u.apellidos}` : u.username}
                        </Text>
                        <Text size="xs" c="dimmed" truncate>@{u.username}</Text>
                      </Stack>
                    </Group>
                    <Button
                      size="xs"
                      variant={u.estadoRelacion === 'NINGUNO' ? 'filled' : 'light'}
                      leftSection={<IconUserPlus size={12} />}
                      onClick={() => enviarSolicitud(u.id)}
                      disabled={u.estadoRelacion !== 'NINGUNO'}
                    >
                      {u.estadoRelacion === 'NINGUNO'
                        ? 'Agregar'
                        : u.estadoRelacion === 'AMIGOS'
                        ? 'Amigos'
                        : u.estadoRelacion === 'SOLICITUD_ENVIADA'
                        ? 'Enviada'
                        : 'Recibida'}
                    </Button>
                  </Group>
                ))}
              </Stack>
            )}
          </Box>
        )}
      </Box>

      <>
      {activeTab === 'recibidas' && (
        <Box pt="md">
          {solicitudesRecibidas.length === 0 ? (
            <Center h={200}>
              <Text color="dimmed">No tienes solicitudes pendientes</Text>
            </Center>
          ) : (
            <Stack gap="sm">
              {solicitudesRecibidas.map((solicitud) => (
                <FilaUsuario
                  key={solicitud.id}
                  nombre={
                    solicitud.remitenteNombre && solicitud.remitenteApellidos
                      ? `${solicitud.remitenteNombre} ${solicitud.remitenteApellidos}`
                      : solicitud.remitenteUsername
                  }
                  username={solicitud.remitenteUsername}
                  avatarUrl={solicitud.remitenteAvatarUrl}
                  onPerfil={() => verPerfil(solicitud.remitenteId)}
                  acciones={
                    <>
                      <Text size="xs" c="dimmed" visibleFrom="sm">
                        {new Date(solicitud.fechaEnvio).toLocaleDateString()}
                      </Text>
                      <Button
                        size="xs"
                        leftSection={<IconUserCheck size={14} />}
                        color="green"
                        onClick={() => aceptarSolicitud(solicitud.id)}
                      >
                        Aceptar
                      </Button>
                      <Button
                        size="xs"
                        leftSection={<IconUserX size={14} />}
                        color="red"
                        variant="outline"
                        onClick={() => rechazarSolicitud(solicitud.id)}
                      >
                        Rechazar
                      </Button>
                    </>
                  }
                />
              ))}
            </Stack>
          )}
        </Box>
      )}

      {activeTab === 'enviadas' && (
        <Box pt="md">
          {solicitudesEnviadas.length === 0 ? (
            <Center h={200}>
              <Text color="dimmed">No has enviado solicitudes</Text>
            </Center>
          ) : (
            <Stack gap="sm">
              {solicitudesEnviadas.map((solicitud) => (
                <FilaUsuario
                  key={solicitud.id}
                  nombre={
                    solicitud.destinatarioNombre && solicitud.destinatarioApellidos
                      ? `${solicitud.destinatarioNombre} ${solicitud.destinatarioApellidos}`
                      : solicitud.destinatarioUsername
                  }
                  username={solicitud.destinatarioUsername}
                  avatarUrl={solicitud.destinatarioAvatarUrl}
                  onPerfil={() => verPerfil(solicitud.destinatarioId)}
                  acciones={
                    <>
                      <Text size="xs" c="dimmed" visibleFrom="sm">
                        Enviada {new Date(solicitud.fechaEnvio).toLocaleDateString()}
                      </Text>
                      <Button
                        size="xs"
                        leftSection={<IconX size={14} />}
                        color="red"
                        variant="outline"
                        onClick={() => cancelarSolicitud(solicitud.id)}
                      >
                        Cancelar
                      </Button>
                    </>
                  }
                />
              ))}
            </Stack>
          )}
        </Box>
      )}

      {activeTab === 'amigos' && (
        <Box pt="md">
          {amigos.length === 0 ? (
            <Center h={200}>
              <Text color="dimmed">Aún no tienes amigos agregados</Text>
            </Center>
          ) : (
            <Stack gap="sm">
              {amigos.map((amistad) => (
                <FilaUsuario
                  key={amistad.id}
                  nombre={
                    amistad.amigo.nombre && amistad.amigo.apellidos
                      ? `${amistad.amigo.nombre} ${amistad.amigo.apellidos}`
                      : amistad.amigo.username
                  }
                  username={amistad.amigo.username}
                  avatarUrl={amistad.amigo.avatarUrl}
                  onPerfil={() => verPerfil(amistad.amigo.id)}
                  acciones={
                    <>
                      <Text size="xs" c="dimmed" visibleFrom="sm">
                        desde {new Date(amistad.creadoEn).toLocaleDateString()}
                      </Text>
                      <Button
                        size="xs"
                        variant="subtle"
                        leftSection={<IconUser size={14} />}
                        onClick={() => verPerfil(amistad.amigo.id)}
                      >
                        Ver perfil
                      </Button>
                      <Button
                        size="xs"
                        color="red"
                        variant="subtle"
                        leftSection={<IconUserX size={14} />}
                        onClick={() => {
                          if (window.confirm('¿Estás seguro de eliminar esta amistad?')) {
                            eliminarAmistad(amistad.id);
                          }
                        }}
                      >
                        Eliminar amigo
                      </Button>
                    </>
                  }
                />
              ))}
            </Stack>
          )}
        </Box>
      )}
      </>
    </PageContainer>
  );
};

export default SolicitudesPage;
